import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { Box, CircularProgress } from '@mui/material'
import { WorkspaceMenu } from '@weberlo/components'

import { ApiError, Workspace, WorkspaceService } from 'generated'
import useAuth from 'hooks/useAuth'
import useConfig from 'hooks/useConfig'
import useToast from 'hooks/useToast'
import AddWorkspace from './Add'

import { Response } from 'types'

import './style.css'

const Workspaces = () => {
  const { toast } = useToast()
  const { workspace, onChangeWorkspace, currency, timezone } = useConfig()

  const { isLoggedIn } = useAuth()

  const [modalOpen, setModalOpen] = useState(false)

  const anchorRef = useRef<any>(null)
  const [open, setOpen] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }
    setOpen(false)
  }

  const { isLoading, data, error, refetch } = useQuery<
    Response<Workspace[]>,
    ApiError
  >({
    queryKey: ['workspaces'],
    enabled: isLoggedIn ? true : false,
    queryFn: () => WorkspaceService.getWorkspaceList() as Response<Workspace[]>,
  })

  const { mutate: create, error: createError } = useMutation({
    mutationFn: (values: { name: string }) =>
      WorkspaceService.postWorkspace(values),
    onSuccess: data => {
      toast({
        message: 'Workspace was created',
        alertColor: 'success',
      })
      refetch()
    },
  })

  useEffect(() => {
    if (error) {
      toast({
        message: error?.message || 'Fetching Error',
        alertColor: 'error',
      })
    }

    if (createError) {
      const issue = createError as ApiError

      toast({
        message:
          issue?.message || 'Workspace creating error. Please try again later',
        alertColor: 'error',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, createError])

  //if there is no workspace
  useEffect(() => {
    if (isLoading || error) return
    if (Number(data?.data?.length) > 0) return

    //check the session storage if user was invited to workspace
    const invitationId = sessionStorage.getItem('invitationId')
    if (invitationId) return

    //create first workspace if there is no workspace and no invitation
    create({ name: 'First workspace' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data])

  useEffect(() => {
    //select first workspace if current workspace is not exist
    if (!data?.data?.[0]?.id) return

    if (!workspace || !data?.data?.some(el => el.id === workspace)) {
      onChangeWorkspace(
        data?.data?.[0]?.id!,
        data?.data?.[0]?.currency!,
        data?.data?.[0]?.timezone!
      )
      return
    }

    if (!currency || !timezone) {
      const ws = data?.data?.find(ws => ws.id === workspace)
      onChangeWorkspace(workspace, ws?.currency!, ws?.timezone!)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, data, workspace, timezone, currency])

  if (isLoading) {
    return (
      <Box className="wbr-button-box">
        <CircularProgress size={25} />
      </Box>
    )
  }

  return (
    <>
      <WorkspaceMenu
        open={open}
        handleClick={handleClick}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        buttonTitle={
          data?.data?.length && workspace
            ? data?.data?.find(ws => ws.id === workspace)?.name
            : ''
        }
        onAddWorkspaceClick={() => {
          setModalOpen(true)
          setOpen(false)
        }}
        workspaces={
          data?.data?.map(el => ({
            name: el.name || '',
            selected: el.id === workspace,
            description: el.user_role || '',
            onWorkspaceClick: () => {
              onChangeWorkspace(el.id!, el.currency!, el.timezone!)
              setTimeout(() => setOpen(false), 300)
            },
          })) || []
        }
      />
      <AddWorkspace
        open={modalOpen}
        close={() => setModalOpen(false)}
      />
    </>
  )
}

export default Workspaces
